import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useHiddenRowsCols
 * Manual hide/unhide for columns and rows.  State is saved per sheet via
 * a ref map so toggling tabs preserves what the user hid.
 */
export default function useHiddenRowsCols({ activeSheet, prevSheetRef }) {
  const hiddenBySheetRef = useRef({});
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [hiddenRows, setHiddenRows] = useState(new Set());

  // Save/restore hidden state across sheet switches.
  useEffect(() => {
    const prevSheet = prevSheetRef?.current;
    if (prevSheet && prevSheet !== activeSheet) {
      hiddenBySheetRef.current[prevSheet] = {
        cols: new Set(hiddenCols),
        rows: new Set(hiddenRows),
      };
    }
    const saved = hiddenBySheetRef.current[activeSheet];
    setHiddenCols(saved?.cols ? new Set(saved.cols) : new Set());
    setHiddenRows(saved?.rows ? new Set(saved.rows) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSheet]);

  const handleHideCol = useCallback((colIndex) => {
    setHiddenCols((prev) => new Set(prev).add(colIndex));
  }, []);

  const handleUnhideCols = useCallback((indices) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      indices.forEach((i) => next.delete(i));
      return next;
    });
  }, []);

  const handleHideRow = useCallback((rowIndex) => {
    setHiddenRows((prev) => new Set(prev).add(rowIndex));
  }, []);

  const handleUnhideRows = useCallback((indices) => {
    setHiddenRows((prev) => {
      const next = new Set(prev);
      indices.forEach((i) => next.delete(i));
      return next;
    });
  }, []);

  // Helpers used by the cell renderer to draw the "hidden item indicator".
  const isLastVisibleBeforeHidden = useCallback(
    (colIndex) => !hiddenCols.has(colIndex) && hiddenCols.has(colIndex + 1),
    [hiddenCols],
  );
  const isLastVisibleBeforeHiddenRow = useCallback(
    (rowIndex) => !hiddenRows.has(rowIndex) && hiddenRows.has(rowIndex + 1),
    [hiddenRows],
  );

  return {
    hiddenCols, hiddenRows,
    handleHideCol, handleUnhideCols, handleHideRow, handleUnhideRows,
    isLastVisibleBeforeHidden, isLastVisibleBeforeHiddenRow,
  };
}
